import React from "react";
import { Link } from "gatsby";
import Layout from "@app/Layout";
import Canonical from "@shared/head/Canonical";
import Breadcrumbs from "@shared/head/Breadcrumbs";
import ButtonBlack from "@shared/uiNew/Button/button_black";
import { MarkedTitle } from "@shared/uiNew/MarkedTitle";
import { SparkStar } from "@shared/uiNew/SparkStar";
import {
  IconBolt,
  IconMagic,
  IconMegaphone,
  IconPieChart,
  IconClock,
  IconFilter,
} from "@shared/uiNew/Card/CardIcons";
import { IconCheck, IconClose } from "@shared/uiNew/Icons/util";
import { TOOLS } from "@shared/arrays/tools";
import { FAQSection } from "@features/tools-pillar/FAQSection";
import {
  TLDR_ROWS,
  CRITERIA,
  CDP_TYPES,
  TOP_3_TOOLS,
  MORE_TOOLS,
  USE_CASES,
  STAGE_GUIDE,
  FAQ_ITEMS,
} from "@shared/arrays/best-ai-cdp-pillar";
import * as s from "./best-ai-cdp.module.scss";

const CDP_TYPE_LABELS: Record<string, string> = {
  Conversational: "Conversational",
  Developer: "Developer",
  Enterprise: "Enterprise",
  Ecommerce: "E-commerce",
  Composable: "Composable",
};

const CRITERIA_ICONS: Record<number, React.FC> = {
  1: IconBolt,
  2: IconMagic,
  3: IconMegaphone,
  4: IconPieChart,
  5: IconClock,
  6: IconFilter,
};

const lookupTool = (slug: string) => TOOLS.find((t) => t.slug === slug);

const PAGE_URL = "https://www.dashly.io/tools/best-ai-cdp/";
const PUBLISHED = "2026-05-29";
const MODIFIED = "2026-05-29";

const collectionPageSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": "https://www.dashly.io/tools/best-ai-cdp/#ai-cdp-hub",
  name: "Best AI CDP tools in 2026: 10 customer data platforms reviewed",
  description:
    "Reviewed 10 AI CDPs on real-time segmentation, predictive scoring, and data activation. G2 ratings, verified pricing, and B2B use cases compared.",
  url: PAGE_URL,
  isPartOf: { "@id": "https://www.dashly.io/tools/#root" },
  about: { "@type": "Thing", name: "AI Customer Data Platform" },
  datePublished: PUBLISHED,
  dateModified: MODIFIED,
  author: {
    "@type": "Person",
    name: "Dmitrii Serg",
    url: "https://www.linkedin.com/in/dimitri-ive/",
  },
  publisher: {
    "@type": "Organization",
    name: "Dashly",
    url: "https://www.dashly.io/",
    logo: {
      "@type": "ImageObject",
      url: "https://www.dashly.io/favicon-32x32.png",
      width: 32,
      height: 32,
    },
  },
  mainEntity: {
    "@type": "ItemList",
    name: "10 best AI CDP tools in 2026",
    numberOfItems: TLDR_ROWS.length,
    itemListElement: TLDR_ROWS.map((row, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: row.profileUrl
        ? `https://www.dashly.io${row.profileUrl}`
        : undefined,
      name: row.toolName,
    })),
  },
});

const faqPageSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((q) => ({
    "@type": "Question",
    name: q.question,
    acceptedAnswer: { "@type": "Answer", text: q.answer },
  })),
});

export const Head = () => (
  <>
    <title>Best AI CDP tools in 2026: 10 platforms reviewed | Dashly</title>
    <meta
      name="description"
      content="Reviewed 10 AI CDPs on real-time segmentation, predictive scoring, and data activation. G2 ratings, verified pricing, and B2B use cases compared."
    />
    <Canonical url={PAGE_URL} />
    <Breadcrumbs
      list={[
        { name: "Dashly", url: "https://www.dashly.io/" },
        { name: "Tools", url: "https://www.dashly.io/tools/" },
        { name: "Best AI CDP", url: PAGE_URL },
      ]}
    />
    <meta name="robots" content="index, follow" />
    <meta
      property="og:title"
      content="Best AI CDP tools in 2026: 10 platforms reviewed | Dashly"
    />
    <meta
      property="og:description"
      content="Reviewed 10 AI CDPs on real-time segmentation, predictive scoring, and data activation. G2 ratings, verified pricing, and B2B use cases compared."
    />
    <meta property="og:url" content={PAGE_URL} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Dashly" />
    <meta property="og:image" content="https://www.dashly.io/dashly-cover.png" />
    <meta property="article:author" content="https://www.linkedin.com/in/dimitri-ive/" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta
      name="twitter:title"
      content="Best AI CDP tools in 2026: 10 platforms reviewed | Dashly"
    />
    <meta name="twitter:image" content="https://www.dashly.io/dashly-cover.png" />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: collectionPageSchema }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: faqPageSchema }}
    />
  </>
);

export default function BestAiCdpPillar({ location }: { location: any }) {
  return (
    <Layout location={location} langKey="en" landing={true} className="new-design">
      <article className={s.pillar}>

        {/* ============ HERO ============ */}
        <section className={s.hero}>
          <div className={s.container}>
            <div className={s.heroCentered}>
              <div className={s.pill}>AI CDPs · 2026 buyer guide</div>
              <h1 className={s.h1}>
                Best AI CDP tools in 2026:{" "}
                <MarkedTitle animationType="highlight">10 platforms reviewed</MarkedTitle>
              </h1>
              <div className={s.heroMeta}>
                <span>May 2026</span>
                <span className={s.heroMetaSep}>·</span>
                <span>20 min read</span>
                <span className={s.heroMetaSep}>·</span>
                <span>MarTech tools</span>
              </div>
              <p className={s.lead}>
                We compared 10 AI customer data platforms across real-time segmentation,
                data activation, and pricing. Every entry shows G2 rating, vendor-verified
                pricing, and a one-line fit statement for your team size and motion. In this
                article, CDP means Customer Data Platform.
              </p>
              <div className={s.heroCta}>
                <ButtonBlack text="See top picks" href="#tldr" />
                <ButtonBlack variant="outline" text="Compare all 10" href="#comparison-table" />
              </div>
            </div>
          </div>
        </section>

        {/* ============ §1 TOP PICKS AT A GLANCE ============ */}
        <section id="tldr" className={s.section}>
          <div className={s.container}>
            <div className={s.glanceHeader}>
              <span className={s.glanceEyebrow}>Verdict in 60 words</span>
              <SparkStar size={22} />
            </div>
            <h2 className={s.h2}>Top picks at a glance</h2>
            <p className={s.body}>
              These three tools cover the most common AI CDP use cases: inbound conversion
              with a conversational data layer, developer-friendly event collection for PLG
              teams, and composable activation from an existing warehouse. The{" "}
              <a href="#comparison-table" className={s.linkInline}>
                full comparison with all 10 tools
              </a>{" "}
              is further down the page.
            </p>
            <ol className={s.glancePicks}>
              {TOP_3_TOOLS.map((t, idx) => (
                <li key={t.toolSlug} className={s.glancePick}>
                  <span className={s.glanceBadge}>{idx + 1}</span>
                  <p className={s.glancePickName}>{t.toolName}</p>
                  <p className={s.glancePickPositioning}>{t.bestFor}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============ §2 WHAT IS AN AI CDP ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>What is an AI CDP?</h2>
            <p className={s.body}>
              A Customer Data Platform collects first-party behavioral data, builds unified
              customer profiles, and makes that data available to downstream marketing and sales
              tools. An AI CDP adds a machine learning layer that goes beyond collection and
              unification: it predicts what a customer will do next, creates audiences from
              those predictions, and activates them in real time through conversations, email,
              or ad audiences.
            </p>
            <p className={s.body}>
              The practical difference shows up in activation speed. A traditional CDP exports
              a segment to your email tool in a nightly batch. An AI CDP triggers a chat
              conversation within 30 seconds of a behavioral event, before intent fades. For
              B2B SaaS teams, that gap between batch and real-time determines whether a trial
              user converts or churns. See how Dashly's{" "}
              <Link to="/lead-profile/" className={s.linkInline}>
                unified lead profiles
              </Link>{" "}
              power this real-time activation layer.
            </p>

            <h3 className={s.h3}>Five CDP types and what makes each one different</h3>
            <div className={s.tableWrap}>
              <table className={s.ioTable}>
                <thead>
                  <tr>
                    <th>CDP type</th>
                    <th>AI capabilities</th>
                    <th>Primary data</th>
                    <th>Activation</th>
                    <th>Best for</th>
                  </tr>
                </thead>
                <tbody>
                  {CDP_TYPES.map((row) => (
                    <tr key={row.cdpType}>
                      <td>
                        <strong>{row.cdpType}</strong>
                      </td>
                      <td>{row.aiCapabilities}</td>
                      <td>{row.primaryData}</td>
                      <td>{row.activation}</td>
                      <td>{row.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className={s.body}>
              The composable CDP model (Hightouch, Census) is the fastest-growing segment in
              2025 because it lets data teams activate their existing warehouse without
              rebuilding a data pipeline. The conversational AI CDP model (Dashly) is the
              fastest-to-activate for B2B SaaS teams that need inbound conversion, not
              warehouse architecture.
            </p>
          </div>
        </section>

        {/* ============ §3 WHAT TO LOOK FOR ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>What to look for in an AI CDP</h2>
            <p className={s.body}>
              Six criteria separate CDPs that generate pipeline from CDPs that generate
              dashboards. These are the questions applied uniformly to every tool in this
              roundup.
            </p>
            <div className={s.evalGrid}>
              {CRITERIA.map((c) => {
                const Icon = CRITERIA_ICONS[c.n];
                return (
                  <div key={c.n} className={s.evalCard}>
                    {Icon ? (
                      <div className={s.evalIcon}>
                        <Icon />
                      </div>
                    ) : null}
                    <span className={s.evalNum}>0{c.n}</span>
                    <h3 className={s.evalTitle}>{c.title}</h3>
                    <p className={s.evalBody}>{c.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ §4 METHODOLOGY ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>Methodology</h2>
            <p className={s.body}>
              We evaluated these 10 tools over 4 weeks using four inputs: live testing on
              real inbound traffic, G2 and Capterra reviews (paraphrased, attributed), pricing
              verified from official vendor sites as of May 2026, and conversations with B2B
              SaaS and e-commerce teams who switched CDP tools in 2025. Where pricing is listed
              as "custom", it reflects vendor quotes gathered directly.
            </p>
            <p className={s.body}>
              We acknowledge bias: Dashly is our own product and appears as position 1 in the
              top 3 picks. The placement reflects our customer outcome data (82%
              conversation-to-meeting at InfluADS) and the inbound-first methodology of this
              review. For teams whose primary use case is data warehouse activation or
              enterprise multi-channel, Hightouch or Adobe Real-Time CDP may be a stronger fit.
            </p>
          </div>
        </section>

        {/* ============ §5 COMPARISON TABLE ============ */}
        <section id="comparison-table" className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>10 AI CDP tools compared</h2>
            <p className={s.body}>
              The table below compares all 10 tools on CDP type, AI layer, pricing, and G2
              rating. For a broader comparison of AI sales tools that activate CDP data, see
              our{" "}
              <Link to="/tools/best-ai-sdr-tools/" className={s.linkInline}>
                best AI SDR tools directory
              </Link>
              .
            </p>
            <div className={s.tableWrap}>
              <table className={s.tldrTable}>
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>Best for</th>
                    <th>CDP type</th>
                    <th>Price from</th>
                    <th>Free</th>
                    <th>G2</th>
                  </tr>
                </thead>
                <tbody>
                  {TLDR_ROWS.map((row) => (
                    <tr
                      key={`compare-${row.toolSlug}`}
                      className={row.featured ? s.featuredRow : undefined}
                    >
                      <td>
                        {row.profileUrl ? (
                          <Link to={row.profileUrl} className={s.toolName}>
                            {row.toolName}
                          </Link>
                        ) : (
                          <span className={s.toolName}>{row.toolName}</span>
                        )}
                      </td>
                      <td>{row.bestFor}</td>
                      <td>
                        <span
                          className={`${s.cdpTypeBadge} ${
                            s["cdpType" + row.cdpType]
                          }`}
                        >
                          {CDP_TYPE_LABELS[row.cdpType] ?? row.cdpType}
                        </span>
                      </td>
                      <td>{row.priceLabel}</td>
                      <td>{row.freeTrial ? "Yes" : "No"}</td>
                      <td>{row.g2Rating}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={s.caption}>
              Prices from vendor sites, May 2026. "Custom" pricing reflects direct vendor
              quotes. G2 ratings as of May 2026.
            </p>
          </div>
        </section>

        {/* ============ §6 TOP 3 PICKS ============ */}
        <section id="top-3-picks" className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>Our top 3 picks for 2026</h2>
            <p className={s.body}>
              These three tools are the most-cited choices among our customer base and B2B SaaS
              growth teams. Each card uses the same structure: positioning, pros and cons from
              real reviews, pricing facts, and a link to the full profile where available.
            </p>

            <div className={s.top6Grid}>
              {TOP_3_TOOLS.map((t) => {
                const tool = lookupTool(t.toolSlug);
                const bg = tool?.bg ?? "#1c1c1c";
                const tx = tool?.tx ?? "#ffffff";
                const initials = tool?.initials ?? t.toolName.slice(0, 2).toUpperCase();
                return (
                  <div
                    key={t.toolSlug}
                    className={`${s.top6Card} ${t.position === 1 ? s.top6Featured : ""}`}
                  >
                    <div className={s.top6Header}>
                      <span className={s.top6LogoBox} style={{ background: bg }}>
                        {tool?.logo ? (
                          <img src={tool.logo} alt={`${t.toolName} logo`} />
                        ) : (
                          <span className={s.top6LogoInitials} style={{ color: tx }}>
                            {initials}
                          </span>
                        )}
                      </span>
                      <span className={s.top6Position}>{t.position}</span>
                      <h3 className={s.top6Name}>{t.toolName}</h3>
                    </div>
                    <p className={s.top6BestFor}>{t.bestFor}</p>
                    <p className={s.body}>{t.intro}</p>

                    <div className={s.top6ProsCons}>
                      <div className={s.top6PcCol}>
                        <p className={s.top6PcLabel}>Pros</p>
                        <ul className={s.top6PcList}>
                          {t.pros.map((p, i) => (
                            <li key={i}>
                              <span className={`${s.top6PcIcon} ${s.pcCheck}`}>
                                <IconCheck size={14} />
                              </span>
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className={s.top6PcCol}>
                        <p className={s.top6PcLabel}>Cons</p>
                        <ul className={s.top6PcList}>
                          {t.cons.map((c, i) => (
                            <li key={i}>
                              <span className={`${s.top6PcIcon} ${s.pcCross}`}>
                                <IconClose size={14} />
                              </span>
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className={s.top6Pricing}>
                      <p className={s.top6PricingLabel}>Pricing</p>
                      <dl className={s.top6PricingTable}>
                        <dt>Starting price</dt>
                        <dd>{t.startingPrice}</dd>
                        <dt>Pricing model</dt>
                        <dd>{t.pricingFacts.pricingModel}</dd>
                        <dt>Free tier</dt>
                        <dd>{t.pricingFacts.freeTrial}</dd>
                        {t.pricingFacts.enterpriseNote && (
                          <>
                            <dt>Enterprise</dt>
                            <dd>{t.pricingFacts.enterpriseNote}</dd>
                          </>
                        )}
                      </dl>
                    </div>

                    {t.outcomesLine && (
                      <p className={s.outcomesLine}>
                        <strong>Customer outcomes:</strong> {t.outcomesLine}
                      </p>
                    )}

                    <div className={s.top6Footer}>
                      <span className={s.top6Meta}>G2 {t.g2Rating}</span>
                      {t.profileUrl && (
                        <Link to={t.profileUrl} className={s.linkInline}>
                          See {t.toolName} profile →
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={s.ctaCallout}>
              <h3>See how Dashly activates your customer data in real time.</h3>
              <p>
                Book a demo to walk through the inbound qualification workflow, from first page
                visit to booked meeting, using the Dashly{" "}
                <Link to="/ai-inbound-revenue-agents/" className={s.linkInline} style={{ color: "#d0ff37" }}>
                  AI Inbound Revenue Agent
                </Link>
                .
              </p>
              <ButtonBlack icon="arrow" text="Book a demo" />
            </div>
          </div>
        </section>

        {/* ============ §7 MORE TOOLS ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>7 more AI CDPs worth considering</h2>
            <p className={s.body}>
              These 7 tools did not make the top 3 picks but are strong choices for specific
              team profiles. Each entry shows CDP type, pricing, pros, cons, and the use case
              where it fits best.
            </p>

            <div className={s.moreGrid}>
              {MORE_TOOLS.map((t) => (
                <div key={t.toolSlug} className={s.moreCard}>
                  <div className={s.moreCardHeader}>
                    <h3 className={s.moreCardName}>{t.toolName}</h3>
                    <span className={`${s.cdpTypeBadge} ${s["cdpType" + t.cdpType]}`}>
                      {CDP_TYPE_LABELS[t.cdpType] ?? t.cdpType}
                    </span>
                  </div>
                  <p className={s.moreCardTagline}>{t.tagline}</p>
                  <p className={s.moreCardMeta}>
                    From {t.startingPrice} · G2 {t.g2Rating}
                  </p>
                  <ul className={s.bullets}>
                    {t.pros.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                  <ul className={s.bullets}>
                    {t.cons.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                  <p className={s.moreCardBestFor}>Best for: {t.bestFor}</p>
                  {t.profileUrl && (
                    <Link to={t.profileUrl} className={s.linkInline}>
                      See {t.toolName} profile →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ §8 BY USE CASE ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>Best AI CDP by use case</h2>
            <p className={s.body}>
              The right AI CDP depends on your industry and motion as much as your team size.
              These four use case profiles match the most common buyer scenarios we see among
              B2B SaaS, e-commerce, enterprise, and data-first teams.
            </p>

            <div className={s.useCaseGrid}>
              {USE_CASES.map((uc) => (
                <div
                  key={uc.id}
                  className={s.useCaseCard}
                  style={{ borderTopColor: uc.color }}
                >
                  <span
                    className={s.useCaseLabel}
                    style={{ background: uc.color, color: uc.labelColor }}
                  >
                    {uc.label}
                  </span>
                  <h3 className={s.useCaseHeading}>{uc.heading}</h3>
                  <p className={s.useCaseBody}>{uc.body}</p>
                  <ul className={s.useCaseTools}>
                    {uc.tools.map((tool, i) => (
                      <li key={i}>{tool}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ §9 HOW TO CHOOSE BY TEAM SIZE ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h2 className={s.h2}>How to choose the right AI CDP for your team</h2>
            <p className={s.body}>
              The right AI CDP depends on your team size and existing data infrastructure as
              much as your channel mix. A composable CDP that saves a Series B data team 40
              hours per month can drain a seed-stage marketer who has no data warehouse to
              connect. For a longer framework on sequencing CDP investments alongside other
              MarTech tools, see the{" "}
              <Link to="/ai-inbound-revenue-agents/" className={s.linkInline}>
                Dashly AI Inbound Revenue Agent overview
              </Link>
              .
            </p>

            <div className={s.stageGrid}>
              {STAGE_GUIDE.map((stage) => (
                <div
                  key={stage.stage}
                  className={s.stageCard}
                  style={{ borderTopColor: stage.color }}
                >
                  <span
                    className={s.stageLabel}
                    style={{ background: stage.color }}
                  >
                    {stage.label}
                  </span>
                  <h3 className={s.h3}>{stage.heading}</h3>
                  <p className={s.body}>{stage.body}</p>
                  <ul className={s.stageTools}>
                    {stage.tools.map((tool, i) => (
                      <li key={i}>{tool}</li>
                    ))}
                  </ul>
                  <p className={s.stageWatchOut}>{stage.watch_out}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <FAQSection
          items={FAQ_ITEMS}
          tag="FAQs"
          subtext="What marketing and data teams ask before choosing an AI CDP. Covers pricing, setup time, CDP types, and how to pick the right fit for your team."
          ctaText="Book a demo"
        />

        {/* ============ RELATED HUBS ============ */}
        <section className={s.section}>
          <div className={s.container}>
            <h3 className={s.h3}>Explore related AI tool directories</h3>
            <p className={s.body}>
              Looking for tools that activate CDP data through AI agents, or a broader sales
              tools directory? These three resources cover adjacent territory.
            </p>
            <div className={s.relatedHubGrid3}>
              <div className={s.relatedHub}>
                <p className={s.relatedHubTag}>Parent directory</p>
                <h4 className={s.h4}>Dashly Tools</h4>
                <p className={s.body}>
                  Browse 25+ AI sales and marketing tools across categories including AI SDR,
                  customer messaging, prospecting, and CDP.
                </p>
                <Link to="/tools/" className={s.linkInline}>
                  Browse all tools →
                </Link>
              </div>
              <div className={s.relatedHub}>
                <p className={s.relatedHubTag}>Related roundup</p>
                <h4 className={s.h4}>Best AI SDR tools</h4>
                <p className={s.body}>
                  How AI SDR agents activate CDP data to qualify inbound leads and book meetings.
                  Includes Dashly, AiSDR, Qualified, and 7 others.
                </p>
                <Link to="/tools/best-ai-sdr-tools/" className={s.linkInline}>
                  Browse Best AI SDR Tools →
                </Link>
              </div>
              <div className={s.relatedHub}>
                <p className={s.relatedHubTag}>Dashly product</p>
                <h4 className={s.h4}>AI Inbound Revenue Agent</h4>
                <p className={s.body}>
                  See how Dashly combines a customer data layer with an AI agent that qualifies,
                  books, and nurtures leads without a separate activation tool.
                </p>
                <Link to="/ai-inbound-revenue-agents/" className={s.linkInline}>
                  See Dashly AI agent →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FINAL CTA ============ */}
        <section className={s.ctaSection}>
          <div className={s.container}>
            <h2 className={s.h2}>
              Activate your customer data with an{" "}
              <MarkedTitle animationType="highlight">AI agent</MarkedTitle>
            </h2>
            <p className={s.body}>
              Dashly collects behavioral data, builds unified lead profiles, and activates
              them through an AI agent that qualifies and books meetings in real time. 14-day
              free trial. No credit card required.
            </p>
            <div className={s.ctaButtons}>
              <ButtonBlack icon="arrow" text="Book a demo" />
              <ButtonBlack
                variant="outline"
                text="Start for free"
                href="/panel/unauthorized/register"
              />
            </div>
          </div>
        </section>

      </article>
    </Layout>
  );
}
